import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap } from 'rxjs';
import { profileActions } from '../actions/profile.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';

@Injectable()
export class ProfileEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getProfile$ = createEffect(() => {
        return this.actions.pipe(
            ofType(profileActions.getProfile),
            mergeMap(() => {
                return this.httpService.getProfile().pipe(
                    finalizeLoader('profile-get'),
                    map((profile) => profileActions.getProfileSuccess({ payload: profile })),
                    catchError((response: HttpErrorResponse) => of(profileActions.getProfileError({ code: response.status }))),
                );
            }),
        );
    });

    updateProfile$ = createEffect(() => {
        return this.actions.pipe(
            ofType(profileActions.updateProfile),
            mergeMap(({ value }) => {
                return this.httpService.updateProfile(value).pipe(
                    finalizeLoader('profile-update'),
                    map((profile) => profileActions.updateProfileSuccess({ payload: profile })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.profile.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.profile.updateError');
                        return of(profileActions.updateProfileError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    changePassword$ = createEffect(() => {
        return this.actions.pipe(
            ofType(profileActions.changePassword),
            mergeMap(({ value }) => {
                return this.httpService.changePassword(value).pipe(
                    finalizeLoader('profile-password'),
                    map(() => profileActions.changePasswordSuccess()),
                    tap(() => this.messageService.add(this.translateService.instant('toast.profile.passwordChanged'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.profile.passwordError');
                        return of(profileActions.changePasswordError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
